/**
 * Migration Service (Frontend)
 * API calls för datamigrering
 */

import api, { getApiBaseUrl } from '../api/client';
import axios from 'axios';
import Cookies from 'js-cookie';
import type { MigrationResult, MigrationPreview } from '@/types/migration';

const API_BASE_URL = getApiBaseUrl();

const migrationApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000,
});

migrationApi.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface DatabaseConnection {
  id: string;
  name: string;
  server: string;
  port: number;
  database: string;
  status?: 'connected' | 'disconnected' | 'testing';
}

export interface ConnectionTestResult {
  connected: boolean;
  error?: string;
}

export interface SourceOrganization {
  o_id: number;
  o_name: string;
  o_pid: number | null;
  o_path: string | null;
  o_depth: number;
  o_topnode: number;
  childCount: number;
}

export const migrationService = {
  getDatabases: async (): Promise<DatabaseConnection[]> => {
    const response = await api.get('/api/sysadmin/migrations/databases');
    return response.data.data;
  },

  testConnection: async (dbId: string): Promise<ConnectionTestResult> => {
    const response = await api.post(`/api/sysadmin/migrations/databases/${dbId}/test`);
    return response.data.data;
  },

  getSourceOrganizations: async (dbId: string): Promise<SourceOrganization[]> => {
    const response = await api.get(`/api/sysadmin/migrations/databases/${dbId}/organizations`);
    return response.data.data;
  },

  runMigration: async (
    databaseId: string,
    migrationId: string,
    dryRun: boolean = true,
    selectedOrgIds: number[] = []
  ): Promise<MigrationResult> => {
    const response = await migrationApi.post(`/api/sysadmin/migrations/${migrationId}/run`, {
      databaseId,
      dryRun,
      selectedOrgIds,
    });
    return response.data.data;
  },

  preview: async (
    databaseId: string,
    selectedOrgIds: number[] = []
  ): Promise<MigrationPreview> => {
    const response = await migrationApi.post('/api/sysadmin/migrations/preview', {
      databaseId,
      selectedOrgIds,
    });
    return response.data.data;
  },

  getMigrationStatus: async (): Promise<Record<string, MigrationResult>> => {
    const response = await api.get('/api/sysadmin/migrations/status');
    return response.data.data;
  },

  getAvailableMigrations: async (): Promise<string[]> => {
    const response = await api.get('/api/sysadmin/migrations');
    return response.data.data;
  },
};

